import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  defaultColDef,
  columnDefs,
  frameworkComponents,
  tableActionsConfig,
  filtering,
  styling,
  serverPagination
} from '../config/table'

import { ns, USER_FEEDBACK_STORE_KEY } from '../constants'
import {
  makeOnOutputRowSelected,
  ModalActions,
  NotificationActions,
  useStoreSelector
} from '@ublique/platform'
import { updateState } from 'utils/state'
import uniqueId from 'lodash/uniqueId'

import { fetchTableData, updateStatus } from '../actions'
import { initialState, reducers, userFeedbackStore } from '../state'
import { getServerSideTableRequestBody } from 'pages/ExtraOutput/utils'
import { storeCollaborationStore } from 'pages/ExtraOutput/state'
import { first, isEmpty, isNull, merge } from 'lodash'
import { userSelectors } from '../state'

const { addNotification } = NotificationActions
const { openModal } = ModalActions

export default function useTableConfig(refreshKpisData) {
  const [gridApi, setGridApi] = useState()
  const [selectedRow, setSelectedRow] = useState()
  const { t } = useTranslation('ExtraOutput')
  const translate = (n) => t(`${ns}.${n}`)

  const selectionParams = useStoreSelector(
    USER_FEEDBACK_STORE_KEY,
    (state) => state.selection
  )
  const isEditable = useStoreSelector(
    USER_FEEDBACK_STORE_KEY,
    (state) => state.isEditable
  )

  const {
    store,
    selectedRooms: rooms,
    allRooms
  } = useStoreSelector('storeCollaboration', (state) => state.filters)

  const uom = useStoreSelector(
    USER_FEEDBACK_STORE_KEY,
    (state) => state.unitOfMeasure
  )

  useEffect(() => {
    if (gridApi) {
      const selectedRows = gridApi?.getSelectedRows()
      if (isEmpty(selectedRows)) {
        setSelectedRow(null)
      } else {
        setSelectedRow(first(selectedRows))
      }
    }
  }, [gridApi, selectionParams, store, rooms])

  const rowSelected = (store, selection, reducer) => {
    const handleSelection = makeOnOutputRowSelected(store, selection, reducer)
    return (row) => {
      handleSelection(row)
    }
  }

  useEffect(() => {
    if (!isEmpty(rooms) && selectionParams.count === 1) {
      const selectedStanza = first(Object.values(selectionParams?.included))
        ?.data?.stanza
      if (selectedStanza && !rooms.map((r) => r.id).includes(selectedStanza)) {
        gridApi.deselectAll()
      }
    }
  }, [gridApi, rooms, selectionParams])

  const selection = {
    rowSelection: 'single',
    suppressRowClickSelection: false,
    onRowSelected: rowSelected(
      userFeedbackStore,
      userSelectors.getSelection,
      reducers.onRowSelection
    )
  }

  const onPaginationSizeChange = (size) => {
    updateState(userFeedbackStore, { pageSize: size })
    gridApi.paginationSetPageSize(size)
    gridApi.purgeServerSideCache()
  }

  const onUomChange = () => {
    updateState(userFeedbackStore, {
      unitOfMeasure: uom === 'value' ? 'items' : 'value'
    })
    gridApi.purgeServerSideCache()
  }

  const changeStatus = async (action) => {
    if (isNull(selectedRow)) return

    const payload = {
      id: selectedRow.id,
      status: selectedRow.stato,
      action
    }
    updateStatus(store, 'feedback', payload).then((res) => {
      if (isEmpty(res)) {
        gridApi.purgeServerSideCache()
        addNotification({
          message: t('EDIT_SUCCESS'),
          kind: 'success'
        })
        onRefreshData()
      }
    })
  }

  useEffect(() => {
    if (gridApi) {
      gridApi.purgeServerSideCache()
    }
  }, [rooms, gridApi])

  const onRefreshData = useCallback(() => {
    if (gridApi) {
      gridApi.purgeServerSideCache()
    }
    refreshKpisData()
  }, [gridApi, refreshKpisData])

  const onExamine = async () => {
    openModal('statusDetail', { onRefreshData, selectedRow, tH: translate })
  }

  const extraHeaderComponent = {
    extraHeaderComponentContainerClassName: 'extraComponent',
    extraHeaderComponent: {
      t,
      selectedRowsCount: selectedRow ? 1 : 0,
      selectedRowsLabel: t(
        'ExtraOutput.StoreCollaboration.SELECTED_ROWS_COUNT',
        {
          rowsCount: selectedRow ? 1 : 0
        }
      ),
      config: tableActionsConfig({
        t: translate,
        onUomChange,
        uom,
        onPaginationSizeChange,
        changeStatus,
        onExamine,
        isEditable
      })
    }
  }

  useEffect(() => {
    if (gridApi) gridApi.purgeServerSideCache()
  }, [store, gridApi])

  const serverSideDatasource = {
    getRows: ({ request, successCallback }) => {
      updateState(userFeedbackStore, { filters: request.filterModel })

      const { store } = storeCollaborationStore.get((state) => state.filters)
      const pageSize = userFeedbackStore.get((state) => state.pageSize)

      //Evito la prima chiamata se lo store non è selezionato
      if (!store) {
        successCallback([], 0)
        return
      }
      const { selectedRooms, allRooms } = storeCollaborationStore.get(
        (state) => state.filters
      )

      const roomsFilter = {
        stanza: {
          values: (isEmpty(selectedRooms) ? allRooms : selectedRooms).map(
            (room) => room.id
          ),
          filterType: 'set'
        }
      }

      const storeFilter = {
        ente: {
          values: [store.id],
          filterType: 'set'
        }
      }

      const filterModel = merge({}, request.filterModel, {
        ...roomsFilter,
        ...storeFilter
      })

      const body = getServerSideTableRequestBody({
        ...request,
        filterModel,
        length: pageSize
      })

      fetchTableData(body, store).then((response) => {
        if (isEmpty(response)) return
        const { items, metadata } = response
        const {
          pagination: { total: rowCount }
        } = metadata

        userFeedbackStore.update((prevState) => ({
          ...prevState,
          filterBody: body,
          selection: {
            ...prevState.selection,
            total: rowCount
          },
          items
        }))
        successCallback(items, rowCount)
      })
    }
  }

  const resetState = () => {
    updateState(userFeedbackStore, initialState)
  }

  useEffect(
    () => () => {
      resetState()
    },
    []
  )

  return [
    {
      config: { sm: 4, md: 8, lg: 12 },
      tableProps: {
        tableTitle: (
          <span className='table-title'>
            {t('UserFeedback.TABLE_TITLE')}
            <span className='uom-indicator'>
              {uom === 'value'
                ? t('UserFeedback.VALUE')
                : t('UserFeedback.KPI.ITEMS')}
            </span>
          </span>
        ),
        key: allRooms?.length + uom,
        getContextMenuItems: [],
        onGridReady: ({ api }) => setGridApi(api),
        columnDefs: columnDefs({ t: translate, store, uom }),
        canShowCheckbox: true,
        serverSideDatasource,
        getRowNodeId: ({ id }) => id ?? uniqueId(),
        rowModelType: 'serverSide',
        frameworkComponents,
        defaultColDef,
        ...serverPagination,
        ...filtering,
        ...selection,
        ...extraHeaderComponent,
        ...styling
      }
    }
  ]
}
